import {
  createAsyncThunk,
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from '@reduxjs/toolkit'
import { ApiError, errorMessage, unwrap } from 'api/call'
import {
  deleteUpdateTasksById,
  getUpdateTasks,
  postUpdateTasks,
  postUpdateTasksByIdRerun,
  postUpdateTasksCacheFlushAll,
  postUpdateTasksCacheRefresh,
  postUpdateTasksRestartServers,
  postUpdateTasksRunAsyncJob,
  postUpdateTasksRunRestoreDump,
  putUpdateTasksByIdRun,
  type JobEnqueuedResponse,
  type PostUpdateTasksData,
  type UpdateTask,
  type UpdateTasksIndexResponse,
} from 'api/generated'
import { toast } from 'sonner'

interface UpdateTasksState {
  loading: boolean
  error: string | null
  update_tasks: UpdateTask[] | null
  page: number
  totalPages: number
}

const initialState: UpdateTasksState = {
  loading: false,
  error: null,
  update_tasks: null,
  page: 1,
  totalPages: 0,
}

// Validation errors come back as `{ errors: { field: [messages] } }`, which
// `errorMessage` can't flatten: join them here.
const validationMessage = (error: unknown, fallback: string) => {
  const errors =
    error instanceof ApiError
      ? (error.body as { errors?: unknown } | undefined)?.errors
      : undefined
  if (errors && typeof errors === 'object' && !Array.isArray(errors)) {
    const messages = Object.values(errors as Record<string, string[]>).flat()
    if (messages.length) return messages.join('\n')
  }
  return errorMessage(error, fallback)
}

export const runRestoreDump = createAsyncThunk<
  void,
  string,
  { rejectValue: string }
>('updateTasks/runRestoreDump', async (dumpFilename, { rejectWithValue }) => {
  try {
    await unwrap(
      postUpdateTasksRunRestoreDump({ body: { dump_filename: dumpFilename } })
    )
    toast.success('Restore dump avviato con successo')
  } catch {
    toast.error('Errore durante il restore del dump')
    return rejectWithValue('Errore durante il restore del dump')
  }
})

export const fetchUpdateTasks = createAsyncThunk<
  UpdateTasksIndexResponse,
  number | undefined,
  { rejectValue: string }
>('updateTasks/fetchUpdateTasks', async (page = 1, { rejectWithValue }) => {
  try {
    return await unwrap(getUpdateTasks({ query: { page } }))
  } catch (error) {
    return rejectWithValue(
      errorMessage(
        error,
        'Si è verificato un errore durante il caricamento degli aggiornamenti.'
      )
    )
  }
})

export const runTask = createAsyncThunk<
  void,
  { id: number; avoidSendMail?: boolean },
  { rejectValue: string }
>('updateTasks/runTask', async ({ id, avoidSendMail }, { rejectWithValue }) => {
  try {
    await unwrap(
      putUpdateTasksByIdRun({
        path: { id },
        body: { avoid_send_mail: avoidSendMail },
      })
    )
    toast.success('Aggiornamento cominciato')
  } catch (error) {
    const message = validationMessage(error, "Errore durante l'esecuzione")
    toast.error(message)
    return rejectWithValue(message)
  }
})

// Launches a task again (`rerunnable` tasks only). A job-created task enqueues
// the same job with the same arguments, which logs a new task; an import file
// is processed again on the same task (`avoidSendMail` only applies there).
export const rerunTask = createAsyncThunk<
  void,
  { task: UpdateTask; avoidSendMail?: boolean },
  { rejectValue: string }
>(
  'updateTasks/rerunTask',
  async ({ task, avoidSendMail }, { dispatch, getState, rejectWithValue }) => {
    try {
      await unwrap(
        postUpdateTasksByIdRerun({
          path: { id: task.id },
          body: { avoid_send_mail: avoidSendMail ?? false },
        })
      )
      toast.success(
        task.job_class
          ? `${task.job_class} rilanciato: il nuovo task comparirà in lista appena il job parte`
          : 'Aggiornamento rilanciato'
      )
      const { page } = (getState() as { update_tasks: UpdateTasksState })
        .update_tasks
      dispatch(fetchUpdateTasks(page))
    } catch (error) {
      const message = validationMessage(error, 'Errore durante il rilancio')
      toast.error(message)
      return rejectWithValue(message)
    }
  }
)

export type SaveUpdateTaskArgs = NonNullable<PostUpdateTasksData['body']> & {
  run_after_save?: boolean
}

export const saveUpdateTask = createAsyncThunk<
  UpdateTask,
  SaveUpdateTaskArgs,
  { rejectValue: string }
>(
  'updateTasks/saveUpdateTask',
  async ({ run_after_save, ...body }, { rejectWithValue }) => {
    toast.message('Salvataggio in corso...')
    try {
      const response = await unwrap(
        postUpdateTasks({ body, query: { run_after_save } })
      )
      // unwrap already threw on `success: false` (ValidationErrorResponse)
      if (!('update_task' in response)) {
        throw new ApiError('Errore durante il salvataggio', 200, response)
      }
      toast.success('Aggiornamento caricato con successo!')
      return response.update_task
    } catch (error) {
      const message = validationMessage(error, 'Errore durante il salvataggio')
      toast.error(message)
      return rejectWithValue(message)
    }
  }
)

export const cancelCacheTask = createAsyncThunk<
  void,
  void,
  { rejectValue: string }
>('updateTasks/cancelCacheTask', async (_, { rejectWithValue }) => {
  toast.message('Cancellazione cache in corso...')
  try {
    await unwrap(postUpdateTasksCacheRefresh())
    toast.success('Task di cancellazione cache lanciato con successo!')
  } catch (error) {
    const message = validationMessage(
      error,
      'Errore durante la cancellazione della cache'
    )
    toast.error(message)
    return rejectWithValue(message)
  }
})

// Emergency: empties everything the backend keeps in Redis, rate limit counters included
export const flushAllCacheTask = createAsyncThunk<
  void,
  void,
  { rejectValue: string }
>('updateTasks/flushAllCacheTask', async (_, { rejectWithValue }) => {
  toast.message('Svuotamento di tutta la cache in corso...')
  try {
    await unwrap(postUpdateTasksCacheFlushAll())
    toast.success('Tutta la cache è stata svuotata!')
  } catch (error) {
    const message = validationMessage(
      error,
      'Errore durante lo svuotamento della cache'
    )
    toast.error(message)
    return rejectWithValue(message)
  }
})

export const restartServersTask = createAsyncThunk<
  void,
  void,
  { rejectValue: string }
>('updateTasks/restartServersTask', async (_, { rejectWithValue }) => {
  toast.message('Restart dei server in corso...')
  try {
    await unwrap(postUpdateTasksRestartServers())
    toast.success('Restart servers lanciato con successo!')
  } catch (error) {
    const message = validationMessage(
      error,
      'Errore durante il restart dei server'
    )
    toast.error(message)
    return rejectWithValue(message)
  }
})

export interface RunAsyncJobArgs {
  job_name: string
  param?: string
  // comes from the form as strings; `id` is converted before sending
  model_param?: { _model?: string; id?: string }
}

export const runAsyncJob = createAsyncThunk<
  JobEnqueuedResponse,
  RunAsyncJobArgs,
  { rejectValue: string }
>(
  'update-tasks/run_async_job',
  async ({ job_name, param, model_param }, { rejectWithValue }) => {
    try {
      const model =
        model_param?._model && model_param.id
          ? { _model: model_param._model, id: Number(model_param.id) }
          : undefined
      const response = await unwrap(
        postUpdateTasksRunAsyncJob({
          query: { job_name, param },
          body: model ? { model_param: model } : undefined,
        })
      )
      toast.success('Job lanciato con successo!')
      return response
    } catch (error) {
      const message = errorMessage(error, 'Errore durante il lancio del job')
      toast.error(message)
      return rejectWithValue(message)
    }
  }
)

export const deleteUpdateTask = createAsyncThunk<
  void,
  number,
  { rejectValue: string }
>('updateTasks/deleteUpdateTask', async (id, { rejectWithValue }) => {
  try {
    await unwrap(deleteUpdateTasksById({ path: { id } }))
    toast.success('Aggiornamento eliminato con successo')
  } catch (error) {
    const message = validationMessage(error, "Errore durante l'eliminazione")
    toast.error(message)
    return rejectWithValue(message)
  }
})

const operations = [
  runRestoreDump,
  runTask,
  rerunTask,
  saveUpdateTask,
  cancelCacheTask,
  flushAllCacheTask,
  restartServersTask,
  runAsyncJob,
  deleteUpdateTask,
] as const

const updateTasksSlice = createSlice({
  name: 'updateTasks',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchUpdateTasks.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchUpdateTasks.fulfilled, (state, action) => {
        state.loading = false
        state.error = null
        state.totalPages = action.payload.total_pages
        state.update_tasks = action.payload.update_tasks
        state.page = action.payload.page
      })
      .addCase(fetchUpdateTasks.rejected, (state, action) => {
        state.loading = false
        state.error =
          action.payload ??
          'Si è verificato un errore durante il caricamento degli aggiornamenti.'
      })
      .addMatcher(isPending(...operations), (state) => {
        state.loading = true
        state.error = null
      })
      .addMatcher(isRejected(...operations), (state, action) => {
        state.loading = false
        state.error =
          action.payload ?? "Si è verificato un errore durante l'operazione."
      })
      // Each thunk shows its own toast: don't add a generic one here, or every
      // action pops two notifications.
      .addMatcher(isFulfilled(...operations), (state) => {
        state.loading = false
        state.error = null
      })
  },
})

export const updateTasksReducer = updateTasksSlice.reducer
